import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProductService, type Product } from '../services/ProductService';
import { ConversaProdutoService } from '../services/ConversaProdutoService';
import { canAccessRoute, getUserPermissions } from '../config/permissions';
import aviso, { textoDoErro } from '../lib/aviso';
import FormDialog from './FormDialog';

interface WinDealProductModalProps {
    isOpen: boolean;
    dealId: number;
    dealTitle: string;
    onConfirm: () => Promise<void>;
    onCancel: () => void;
}

const emReais = (v: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

/**
 * Vincular o produto vendido antes de fechar a venda.
 *
 * Redesenhado em 2026-10-11 sobre o `FormDialog`, junto com o
 * `LossReasonModal` — os dois abrem do mesmo lugar (a troca de etapa na
 * Pipeline, no detalhe e nas Tarefas) e eram duas molduras diferentes para o
 * mesmo gesto. Prefixo `wdpm-` aposentado com o CSS.
 *
 * **O que saiu, e por quê:**
 *
 * - **A faixa amarela** *"Para fechar esta venda é necessário vincular pelo
 *   menos um produto ou serviço"*: regra, não aviso. Virou a linha de apoio.
 * - **O ícone da caixa aberta** e o **botão verde**. Verde não é cor de ação
 *   do produto — o primário é o navy, como em todo o resto.
 * - **O campo de busca quando não há o que buscar.** Agora ele só aparece a
 *   partir de 8 produtos, que é o mesmo limiar do `searchFrom` do Select: com
 *   meia dúzia de itens à vista, filtrar é mais trabalho do que olhar.
 *
 * **E a lista vazia ganhou porta de saída.** Sem produto cadastrado, este
 * diálogo era um beco sem saída: a venda não fecha e a tela não diz o que
 * fazer. Agora leva ao cadastro — e só para quem tem acesso a ele.
 */
const WinDealProductModal = ({ isOpen, dealId, dealTitle, onConfirm, onCancel }: WinDealProductModalProps) => {
    const navigate = useNavigate();
    const [products, setProducts] = useState<Product[]>([]);
    const [isLoadingProducts, setIsLoadingProducts] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
    const [quantity, setQuantity] = useState(1);
    const [unitPrice, setUnitPrice] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        setSelectedProductId(null);
        setQuantity(1);
        setUnitPrice('');
        setSearchQuery('');
        setIsSaving(false);

        const loadProducts = async () => {
            setIsLoadingProducts(true);
            try {
                const res = await ProductService.getAll();
                if (res.sucesso) setProducts(res.dados ?? []);
            } catch {
                aviso.erro('Não foi possível carregar os produtos.');
            } finally {
                setIsLoadingProducts(false);
            }
        };
        loadProducts();
    }, [isOpen]);

    const filtered = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return products;
        return products.filter(p =>
            p.nome.toLowerCase().includes(q) ||
            p.codigo.toLowerCase().includes(q) ||
            (p.marca && p.marca.toLowerCase().includes(q))
        );
    }, [products, searchQuery]);

    const selectedProduct = products.find(p => p.produtoId === selectedProductId);

    /* Mesmo limiar do `searchFrom` do Select: abaixo disso a lista inteira cabe
       na vista e o campo de busca só rouba uma linha. */
    const temBusca = products.length >= 8;
    const semProdutos = !isLoadingProducts && products.length === 0;
    const podeCadastrar = useMemo(
        () => (isOpen ? canAccessRoute('/products', getUserPermissions()) : false),
        [isOpen],
    );

    /* O que vai ser gravado, em uma linha — o preço negociado é editável, então
       o total é a única forma de ver o que a venda registra antes de confirmar. */
    const precoEfetivo = selectedProduct
        ? (parseFloat(unitPrice) >= 0 ? parseFloat(unitPrice) : selectedProduct.preco)
        : 0;
    const resumo = selectedProduct
        ? `${quantity} × ${emReais(precoEfetivo)} = ${emReais(precoEfetivo * quantity)}`
        : null;

    const handleConfirm = async () => {
        if (!selectedProduct) return;
        setIsSaving(true);
        try {
            await ConversaProdutoService.vincular(dealId, {
                produtoId: selectedProduct.produtoId,
                quantidade: quantity,
                precoUnitarioNegociado: precoEfetivo,
            });
            await onConfirm();
        } catch (err: any) {
            aviso.erro(textoDoErro(err, 'Não foi possível vincular o produto.'));
            setIsSaving(false);
        }
    };

    return (
        <FormDialog
            open={isOpen}
            onClose={onCancel}
            titulo={`Fechar a venda de «${dealTitle}»?`}
            sub="Toda venda fechada registra o que foi vendido."
            largura={520}
            rodapeTexto={resumo}
            confirmLabel="Vincular e fechar"
            busyLabel="Vinculando…"
            confirmDisabled={!selectedProductId}
            busy={isSaving}
            onConfirm={handleConfirm}
        >
            {temBusca && (
                <div className="ds-fd-campo">
                    <label className="ds-fd-lab" htmlFor="wdpm-busca">Buscar produto</label>
                    <input
                        id="wdpm-busca"
                        type="text"
                        className="ds-fd-inp"
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        placeholder="Nome, marca ou código"
                        autoFocus
                        disabled={isSaving}
                    />
                </div>
            )}

            <div className="ds-fd-campo">
                <label className="ds-fd-lab" id="wdpm-rot-lista">
                    Produto ou serviço <i aria-hidden="true">*</i>
                </label>
                <div className="ds-fd-escolha" role="listbox" aria-labelledby="wdpm-rot-lista">
                    {isLoadingProducts ? (
                        <p className="ds-fd-vazio">Carregando produtos…</p>
                    ) : filtered.length === 0 ? (
                        <p className="ds-fd-vazio">
                            {semProdutos
                                ? (podeCadastrar
                                    ? 'Nenhum produto cadastrado ainda. Cadastre o que a sua empresa vende para registrar as vendas fechadas.'
                                    : 'Nenhum produto cadastrado. Peça a um administrador para cadastrar o que a empresa vende.')
                                : `Nenhum produto com «${searchQuery.trim()}».`}
                        </p>
                    ) : (
                        filtered.map(p => {
                            const escolhido = selectedProductId === p.produtoId;
                            return (
                                <button
                                    key={p.produtoId}
                                    type="button"
                                    role="option"
                                    aria-selected={escolhido}
                                    className={`ds-fd-op ${escolhido ? 'is-on' : ''}`}
                                    disabled={isSaving}
                                    onClick={() => {
                                        setSelectedProductId(p.produtoId);
                                        setUnitPrice(p.preco.toString());
                                    }}
                                >
                                    <span className="ds-fd-op-txt">
                                        <span className="ds-fd-op-nome">{p.nome}</span>
                                        <span className="ds-fd-op-meta">
                                            {p.codigo}{p.marca ? ` · ${p.marca}` : ''}
                                        </span>
                                    </span>
                                    <span className="ds-fd-op-val">{emReais(p.preco)}</span>
                                </button>
                            );
                        })
                    )}
                </div>

                {semProdutos && podeCadastrar && (
                    <button
                        type="button"
                        className="ds-fd-btn ds-fd-porta"
                        onClick={() => { onCancel(); navigate('/products'); }}
                        disabled={isSaving}
                    >
                        Cadastrar produtos
                    </button>
                )}
            </div>

            {selectedProduct && (
                <div className="ds-fd-linha">
                    <div className="ds-fd-campo">
                        <label className="ds-fd-lab" htmlFor="wdpm-qtd">Quantidade</label>
                        <input
                            id="wdpm-qtd"
                            type="number"
                            min="1"
                            className="ds-fd-inp"
                            value={quantity}
                            onChange={e => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                            disabled={isSaving}
                        />
                    </div>
                    <div className="ds-fd-campo">
                        <label className="ds-fd-lab" htmlFor="wdpm-preco">Preço negociado (R$)</label>
                        <input
                            id="wdpm-preco"
                            type="number"
                            step="0.01"
                            min="0"
                            className="ds-fd-inp"
                            value={unitPrice}
                            onChange={e => setUnitPrice(e.target.value)}
                            placeholder={selectedProduct.preco.toString()}
                            disabled={isSaving}
                        />
                    </div>
                </div>
            )}
        </FormDialog>
    );
};

export default WinDealProductModal;
